// src/hooks/auth/useAuth.js
import { useState, useContext, createContext, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import * as authApi from "../../api/auth";

const AuthContext = createContext();

function decodeJwt(token) {
  try {
    // handle base64url safely
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getUserFromToken() {
  const token = localStorage.getItem("accessToken");
  if (!token) return null;
  const payload = decodeJwt(token);
  if (!payload) return null;

  return {
    id: payload.user_id,
    username: payload.username,
    email: payload.email,
    is_staff: payload.is_staff,
    is_superuser: payload.is_superuser,
    groups: payload.groups || [],
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => getUserFromToken());
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => setLoading(false), []);

  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: ({ data }) => {
      const { access, refresh } = data;
      localStorage.setItem("accessToken", access);
      localStorage.setItem("refreshToken", refresh);

      const payload = decodeJwt(access) || {};
      setUser({
        id: payload.user_id,
        username: payload.username,
        email: payload.email,
        is_staff: payload.is_staff,
        is_superuser: payload.is_superuser,
        groups: payload.groups || [],
      });
      setError(null);
    },
    onError: (err) => setError(err.response?.data?.detail || "Login failed"),
  });

  const logout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, error, loading, login: loginMutation.mutate, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
